/**
 * Minimal IndexedDB wrapper.
 *
 * Hand-rolled rather than pulling in a library: this is about 120 lines, it is
 * the store that holds a salesperson's unsent orders, and I would rather own
 * that code than depend on a package for it.
 *
 * IndexedDB — not localStorage — because the offline queue holds structured
 * records and compressed photos. localStorage is synchronous (it blocks the UI
 * thread), caps out around 5 MB, and stores strings only.
 */

const DB_NAME = 'gd-strategic';
const DB_VERSION = 1;

export type StoreName =
  /** Unsent mutations: orders, visits, PODs, collections. Drained on reconnect. */
  | 'outbox'
  /** Read-through cache so the app opens with data while offline. */
  | 'cache'
  /** Photos captured offline, held until their parent record syncs. */
  | 'media'
  /** Session, locale, last-known user. */
  | 'kv';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains('outbox')) {
        const outbox = db.createObjectStore('outbox', { keyPath: 'id' });
        // Drained oldest-first so a shop's order reaches the server before the
        // POD for the same shop.
        outbox.createIndex('byCreatedAt', 'createdAt');
        outbox.createIndex('byStatus', 'status');
      }
      if (!db.objectStoreNames.contains('cache')) {
        const cache = db.createObjectStore('cache', { keyPath: 'key' });
        cache.createIndex('byExpiry', 'expiresAt');
      }
      if (!db.objectStoreNames.contains('media')) {
        db.createObjectStore('media', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('kv')) {
        db.createObjectStore('kv', { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    // A second tab holding an old version would block the upgrade forever.
    request.onblocked = () => reject(new Error('IndexedDB upgrade blocked by another tab'));
  });

  return dbPromise;
}

function tx<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const request = fn(transaction.objectStore(store));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.onabort = () => reject(transaction.error);
      }),
  );
}

export async function put<T>(store: StoreName, value: T): Promise<void> {
  await tx(store, 'readwrite', (s) => s.put(value as unknown as never));
}

export async function get<T>(store: StoreName, key: string): Promise<T | undefined> {
  return tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
}

export async function del(store: StoreName, key: string): Promise<void> {
  await tx(store, 'readwrite', (s) => s.delete(key));
}

export async function all<T>(store: StoreName): Promise<T[]> {
  return tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
}

export async function clear(store: StoreName): Promise<void> {
  await tx(store, 'readwrite', (s) => s.clear());
}

/** Rows from an index, in index order. */
export async function byIndex<T>(
  store: StoreName,
  indexName: string,
  query?: IDBValidKey | IDBKeyRange,
): Promise<T[]> {
  return open().then(
    (db) =>
      new Promise<T[]>((resolve, reject) => {
        const transaction = db.transaction(store, 'readonly');
        const request = transaction.objectStore(store).index(indexName).getAll(query);
        request.onsuccess = () => resolve(request.result as T[]);
        request.onerror = () => reject(request.error);
      }),
  );
}

// ---------------------------------------------------------------------------
// Simple key/value helpers
// ---------------------------------------------------------------------------

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const row = await get<{ key: string; value: T }>('kv', key);
  return row?.value;
}

export async function kvSet<T>(key: string, value: T): Promise<void> {
  await put('kv', { key, value });
}

export async function kvDel(key: string): Promise<void> {
  await del('kv', key);
}

// ---------------------------------------------------------------------------
// Read-through cache
// ---------------------------------------------------------------------------

interface CacheRow<T> {
  key: string;
  value: T;
  cachedAt: number;
  expiresAt: number;
}

export async function cacheGet<T>(key: string): Promise<{ value: T; stale: boolean } | undefined> {
  const row = await get<CacheRow<T>>('cache', key);
  if (!row) return undefined;
  // Expired entries are returned as STALE rather than dropped: showing
  // yesterday's catalogue with a "last updated" note beats showing nothing at
  // all in a shop with no signal.
  return { value: row.value, stale: Date.now() > row.expiresAt };
}

export async function cacheSet<T>(key: string, value: T, ttlSeconds = 3600): Promise<void> {
  await put<CacheRow<T>>('cache', {
    key,
    value,
    cachedAt: Date.now(),
    expiresAt: Date.now() + ttlSeconds * 1000,
  });
}

/** Drop everything the signed-in user could see. Called on logout and remote wipe. */
export async function wipeUserData(): Promise<void> {
  await Promise.all([clear('cache'), clear('outbox'), clear('media'), clear('kv')]);
}

/** Rough storage usage, shown on the sync screen. */
export async function storageEstimate(): Promise<{ usedMb: number; quotaMb: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return {
    usedMb: Math.round((usage / 1048576) * 10) / 10,
    quotaMb: Math.round((quota / 1048576) * 10) / 10,
  };
}
